/**
 * types.ts — Campaign Planner TypeScript types
 *
 * Mirrors the backend API shapes from campaignController.ts exactly.
 * Frontend step state machine lives in index.tsx; these are pure data types.
 */

export type CampaignStatus =
  | 'draft' | 'arc_locked' | 'generating' | 'rendering'
  | 'locked' | 'active' | 'paused' | 'completed' | 'cancelled';

export type PostStatus =
  | 'pending' | 'publishing' | 'published' | 'failed' | 'skipped' | 'paused';

export type GoalKey = 'grow_followers' | 'generate_leads' | 'build_authority';

export type AngleKey = 'thoughtful' | 'contrarian' | 'bold' | 'viral';

export type SchedulePattern =
  | 'daily' | 'weekdays' | 'every_other_day' | 'mwf' | 'tt' | 'weekly' | 'custom';

export type WeekDay = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

// ── Viability types — mirror campaignViabilityService.ts shapes ───────────────

export type ArcTemplateKey = 'journey' | 'argument' | 'hybrid';

export interface ArcTemplateOption {
  key:         ArcTemplateKey;
  name:        string;
  description: string;
  fit:         'best' | 'good' | 'possible';
}

export interface ViabilityResult {
  viability:             'campaign' | 'short_series' | 'single_post';
  minDays:               number;
  maxDays:               number;
  suggestedDays:         number;
  bestArc:               ArcTemplateKey | null;
  viableArcs:            ArcTemplateOption[];
  enrichmentSuggestions: string[];
  reason:                string;
}

// ── Arc slot — matches ArcSlot from campaignArcService.ts ────────────────────

export interface ArcSlot {
  dayNumber:     number;
  role:          string;
  title:         string;
  angle:         AngleKey;
  narrativeNote: string;
  openingHook:   string;
  perspective?:  'first_person' | 'observer';  // present on new arcs; old arcs may omit
}

// ── Post ─────────────────────────────────────────────────────────────────────

export interface CampaignPost {
  id:               number;
  dayNumber:        number;
  captionText:      string;
  templateId:       string;
  scheduledAt:      string;   // ISO 8601 UTC
  status:           PostStatus;
  linkedinShareUrl: string | null;
  linkedinShareUrn: string | null;
  publishedAt:      string | null;
  lastError:        string | null;
  retryCount:       number;
  slides?:          unknown[];  // ContentSlide[] — present in GET /api/campaigns/:id response
}

// ── Arc template display meta (mirrors ARC_TEMPLATE_META in campaignViabilityService) ──

export const ARC_TEMPLATE_META: Record<ArcTemplateKey, { name: string; description: string }> = {
  journey: {
    name:        'Personal Journey Arc',
    description: 'Tell your story chapter by chapter. Works best when you have lived experience — a failure, a pivot, a discovery.',
  },
  argument: {
    name:        'Argument Arc',
    description: 'Build a case day by day. Start with a problem, present evidence, flip the conventional wisdom, deliver a framework.',
  },
  hybrid: {
    name:        'Hybrid Arc',
    description: 'Open with the big picture, descend into your personal experience as proof, then zoom back out with implications.',
  },
};

// ── Campaign ─────────────────────────────────────────────────────────────────

export interface Campaign {
  id:              number;
  name:            string;
  topic:           string;
  goal:            GoalKey | null;
  status:          CampaignStatus;
  corePrinciple:   string;
  storyArc:        ArcSlot[];    // the raw arc array (backend returns this separate from corePrinciple)
  arcTemplate:     ArcTemplateKey | null;   // journey | argument | hybrid — for arc review UI
  totalDays:       number;
  dailyTime:       string;       // "09:00"
  timezone:        string;
  schedulePattern: SchedulePattern;
  scheduleDays:    WeekDay[] | null;
  templateIds:     string[];
  colorTheme:      string;
  posts:           CampaignPost[];
}

// ── Campaign list summary (Dashboard) ─────────────────────────────────────────
// Shape returned by GET /api/campaigns — enriched with progress info.

export interface CampaignSummary {
  id:              number;
  name:            string;
  topic:           string;
  status:          CampaignStatus;
  totalDays:       number;
  dailyTime:       string;
  timezone:        string;
  schedulePattern: SchedulePattern;
  scheduleDays:    WeekDay[] | null;
  createdAt:       string;
  totalPosts:      number;
  publishedPosts:  number;
  pendingPosts:    number;
  nextPostAt:      string | null;
  lastPublishedAt: string | null;
}

// ── Create campaign request ───────────────────────────────────────────────────

export interface CreateCampaignBody {
  topic:           string;
  goal:            GoalKey;
  tonePreference:  AngleKey;
  totalDays:       number;
  templateIds:     string[];
  colorTheme:      string;
  dailyTime:       string;
  timezone:        string;
  schedulePattern: SchedulePattern;
  scheduleDays?:   WeekDay[];
  arcTemplate?:    ArcTemplateKey;   // selected arc template from viability assess step
}

// ── POST /api/campaigns response ─────────────────────────────────────────────

export interface CreateCampaignResponse {
  id:            number;
  name:          string;
  status:        CampaignStatus;
  storyArc:      ArcSlot[];
  corePrinciple: string;
  arcTemplate:   ArcTemplateKey | null;
}

// ── Arc Evaluator ─────────────────────────────────────────────────────────────

export interface SlotEvaluation {
  dayNumber:  number;
  quality:    'strong' | 'thin' | 'weak';
  critique:   string;
  suggestion: string;
}

export interface EvaluationResult {
  verdict:           'strong' | 'thin' | 'weak';
  overallCritique:   string;
  overallSuggestion: string;
  enrichmentPrompt:  string;   // bullet-pointed questions to guide author's free-text input
  slots:             SlotEvaluation[];
}

// Quality color palette — distinct from role colors
export const QUALITY_COLORS: Record<'strong' | 'thin' | 'weak', string> = {
  strong: '#2DB88F',
  thin:   '#E8A24F',
  weak:   '#E05252',
};

// ── Step machine ─────────────────────────────────────────────────────────────

export type PlannerStep = 'brief' | 'arc-review' | 'lock-progress' | 'dashboard';

// ── Role display map ─────────────────────────────────────────────────────────

export const ROLE_LABELS: Record<string, string> = {
  hook_tension:          'Hook / Tension',
  stakes_proof:          'Stakes / Proof',
  root_cause:            'Root Cause',
  reframe:               'Reframe',
  proof_point:           'Proof Point',
  actionable_framework:  'Framework',
  resolution_cta:        'Resolution / CTA',
};

// Badge hue per role — distinct colors cycling through the accent palette
export const ROLE_COLORS: Record<string, string> = {
  hook_tension:          '#5B4FE8',
  stakes_proof:          '#E87B4F',
  root_cause:            '#2DB88F',
  reframe:               '#C94F7C',
  proof_point:           '#4FA8E8',
  actionable_framework:  '#A0892F',
  resolution_cta:        '#7B5BE8',
};

// ── Timezone options ─────────────────────────────────────────────────────────

export const TIMEZONE_OPTIONS = [
  { value: 'Asia/Kolkata',      label: 'IST — Asia/Kolkata (UTC+5:30)' },
  { value: 'America/New_York',  label: 'EST — New York (UTC−5)' },
  { value: 'America/Chicago',   label: 'CST — Chicago (UTC−6)' },
  { value: 'America/Denver',    label: 'MST — Denver (UTC−7)' },
  { value: 'America/Los_Angeles', label: 'PST — Los Angeles (UTC−8)' },
  { value: 'Europe/London',     label: 'GMT — London (UTC+0)' },
  { value: 'Europe/Paris',      label: 'CET — Paris (UTC+1)' },
  { value: 'Europe/Berlin',     label: 'CET — Berlin (UTC+1)' },
  { value: 'Asia/Singapore',    label: 'SGT — Singapore (UTC+8)' },
  { value: 'Asia/Tokyo',        label: 'JST — Tokyo (UTC+9)' },
  { value: 'Australia/Sydney',  label: 'AEST — Sydney (UTC+10)' },
  { value: 'UTC',               label: 'UTC' },
];
